import { cn, HairlineDivider } from '@lashpic/ui/admin';
import { useSidebarReveal } from '@lashpic/ui/motion';
import { LogOut } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { NAV_GROUPS } from '../lib/nav-config';
import { CommandPalette } from './CommandPalette';

/**
 * 관리자 콘솔 사이드바 — dashfolio DNA 기반.
 *
 * 특징:
 *   - 좌측 256px 고정, 불투명 surface (dashfolio 는 Vibrancy 미사용)
 *   - 프로필 블록(원형 이니셜 + 이름) + ⌘K 팔레트 + 섹션 네비 + 로그아웃
 *   - 각 네비 아이템 우측에 순차 숫자 키보드 힌트 (1..9)
 *   - Active 상태는 라벤더 accent-soft + accent text (dashfolio-dna.md §2.1)
 *
 * 기준: docs/design/dashfolio-dna.md §3.3
 */

const SIDEBAR_WIDTH_PX = 256;

// 모든 네비 아이템에 순차 digit 매핑 (1부터) — 키보드 힌트 표시용
const NAV_DIGIT_MAP = new Map<string, number>(
  NAV_GROUPS.flatMap((g) => g.items).map((item, idx) => [item.path, idx + 1]),
);

export function Sidebar() {
  const revealRef = useSidebarReveal<HTMLElement>();

  return (
    <aside
      className="flex h-screen shrink-0 flex-col border-r border-[var(--color-admin-hairline)] bg-[var(--color-admin-sidebar)]"
      style={{ width: SIDEBAR_WIDTH_PX }}
      role="navigation"
      aria-label="관리자 내비게이션"
    >
      {/* 프로필 블록 (상단) */}
      <div className="flex items-center gap-3 px-4 py-4">
        <div
          className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-admin-accent-soft)] text-[12px] font-semibold text-[var(--color-admin-accent)]"
          aria-hidden
        >
          LA
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[13px] font-semibold tracking-tight text-[var(--color-admin-text)]">
            Lashpic Admin
          </div>
          <div className="truncate text-[11px] text-[var(--color-admin-text-tertiary)]">
            운영자 콘솔
          </div>
        </div>
      </div>

      <HairlineDivider decorative />

      {/* ⌘K 팔레트 트리거 */}
      <div className="px-3 py-3">
        <CommandPalette />
      </div>

      <HairlineDivider decorative />

      {/* 섹션 네비 */}
      <nav
        ref={revealRef}
        className="flex-1 overflow-y-auto px-2 py-2"
        aria-label="기본 내비게이션"
      >
        {NAV_GROUPS.map((group) => (
          <div key={group.section} className="mt-5 first:mt-1">
            <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-admin-text-tertiary)]">
              {group.heading}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const digit = NAV_DIGIT_MAP.get(item.path);
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      data-sidebar-item
                      className={({ isActive }) =>
                        cn(
                          'admin-focus-ring flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium transition-colors duration-[var(--duration-admin-instant)]',
                          isActive
                            ? 'bg-[var(--color-admin-accent-soft)] text-[var(--color-admin-accent)]'
                            : 'text-[var(--color-admin-text)] hover:bg-[var(--color-admin-surface-2)]',
                        )
                      }
                    >
                      <Icon className="size-4 shrink-0" aria-hidden />
                      <span className="flex-1 truncate">{item.label}</span>
                      {digit !== undefined ? (
                        <kbd className="admin-kbd-hint" aria-hidden>
                          {digit}
                        </kbd>
                      ) : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <HairlineDivider decorative />

      {/* 푸터 · 로그아웃 */}
      <div className="px-4 py-3">
        <button
          type="button"
          onClick={() => {
            // Cloudflare Access 세션 종료
            window.location.assign('/cdn-cgi/access/logout');
          }}
          className="admin-focus-ring flex items-center gap-1.5 rounded text-[11px] text-[var(--color-admin-text-secondary)] transition-colors hover:text-[var(--color-admin-text)]"
        >
          <LogOut className="size-3" aria-hidden />
          <span>로그아웃</span>
        </button>
      </div>
    </aside>
  );
}
